/**
 * Dev-only funnel-events visibility (owner 2026-07-16): console.log every
 * emitted event so authors/devs can SEE the system firing. Gated per-call by
 * `isFunnelUiEventsDebugEnabled()` (env-config turns it on for dev only;
 * `?funnel_ui_events_debug=1` overrides per session), so it is runtime-
 * flippable and adds zero noise in prod.
 *
 * Intentionally uses console.log (not funnelLogger): this is a human debugging
 * tap, meant to be read in the browser console next to the page.
 */

import type { FunnelEventBus } from './funnel-event-bus';
import type { TssEventEnvelope } from './envelope';
import { isFunnelUiEventsDebugEnabled } from './feature-flag';

const TAG = '[funnel-events]';

/** Log a funnel-events debug line (no-op unless the debug flag is on). */
export function debugLogFunnelEvent(kind: string, detail: Record<string, unknown>): void {
  if (!isFunnelUiEventsDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`${TAG} ${kind}`, detail);
}

/**
 * Tap the bus and log every emission: name, hop, trigger/block/step context and
 * params. Attach alongside the analytics adapter in the provider.
 */
export function attachFunnelEventsDebugLogger(bus: FunnelEventBus): { unsubscribe: () => void } {
  return bus.on('*', (envelope: TssEventEnvelope) => {
    if (!isFunnelUiEventsDebugEnabled()) return;
    const payload = envelope.payload as
      | { params?: Record<string, unknown>; context?: Record<string, unknown> }
      | undefined;
    // eslint-disable-next-line no-console
    console.log(`${TAG} emit "${envelope.name}"`, {
      hop: envelope.metadata.hopCount ?? 0,
      phase: envelope.metadata.phase,
      origin: envelope.metadata.origin,
      stepId: payload?.context?.stepId,
      blockId: payload?.context?.blockId,
      trigger: payload?.context?.trigger,
      params: payload?.params,
      eventId: envelope.metadata.eventId,
      correlationId: envelope.metadata.correlationId,
      sessionId: envelope.metadata.sessionId,
    });
  });
}
